import { createAsyncThunk, createSlice, PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { Afr, AfrVersionStateChange, EventGeneric } from '../redux/AfrSlice';
import { Entity } from '../redux/EntitySlice';
import http from '../utils/http';
import utilityHelper from '../utils/utilityHelper';
import { PostCommentRequest } from './AfrReviewSlice';
import { Auditor, AuditorDetailsInput } from './AuditorSlice';
import { RootState } from './store';
import { logerUser } from './UserSlice';

export interface EventPerson {
    id: string;
    name: string;
    lastName: string;
    login: string;
}

export interface Connector {
    entityId: string;
    afrYear: number;
    version: number;
}

export interface EMail {
    recipient: logerUser;
    subject: string;
    body: string;
}

export interface afrEmailStage {
    order: number;
    reportLink: string;
    numberContacted: number;
    subject: string;
    emailContent: string;
    taxonomyType: string;
    autoReminderEmailDate: string;
    nextEmailDeliveryDate: string;
    nextEmailDeliveryText: string;
}

export interface afrEmailStageInput {
    order: number;
    reportLink: string;
    numberContacted: number;
    subject: string;
    emailContent: string;
    taxonomyType: string; 
    autoReminderEmailDate: string;
    nextEmailDeliveryDate: string;
    nextEmailDeliveryText: string;
}

export interface autoReminderEmailScheduler {
    reportType: string;
    scheduledMonthDate: string;
    updatedBy: number;
}


export interface MessageEvent {
    connector: string;
    creator: EventPerson;
    timestamp: string;
    role: string;
    message: EMail;
    status: string;
    payload: string | null;
    /*  Sent: the email has been received on our servers and is awaiting processing.
        Delivered: the email was sent to the client's mailbox.
        Rejected: the email was rejected by our platform due to inappropriate content or because it was about to be sent to a blacklisted address.
        Bounced: Once the email has been sent, the recipient's mail server sent us a return code back specifying the email could not be delivered.
        SPAM: recipients are always able to report an unsollicited email as SPAM.
    */
}

export interface ErrorData {
    error: any;
    errorCapture: string | null;
}

export interface DiscrepancyEvent {
    afrId: string;
    entityName: string;
    firstDiscrepancy: MessageEvent | null;
    secondDiscrepancy: MessageEvent | null;
    thirdDiscrepancy: MessageEvent | null;
}

export interface AssignmentEvent {
    connector: string;
    creator: logerUser;
    timestamp: string;
    role: string;
    assignee: logerUser;

}

export interface CommentEvent {
    connector: string;
    creator: logerUser;
    timestamp: string;
    role: string;
    comment: string;

}

export interface AfrEvents {
    entityId: string;
    afrYear: number;
    messageEvents: Array<MessageEvent>;
    assignmentEvents: Array<AssignmentEvent>;
    commentEvents: Array<CommentEvent>;
    versionEvents: Array<AfrVersionStateChange>;
}

export interface EmailInput {
    recipientId: string;
    subject: string;
    body: string;
}

export interface EmailEventInput {
    connector: string;
    payload: string;
    message: EmailInput;
    isResponse: boolean;
    role: string;
}

export interface ActionRequest {
    actionCode: string;
    connector: string;
}

export interface ActionResponseRequest {
    actionCode: string;
    connector: string;
    response: string;
    recipientId: string;
    reason: string | null;
}

export interface MultipleBypassResponseRequest {
    multipleRequest: ActionResponseRequest[];
}


export interface AccountWithCode {
    account: string;
    objectcode: string;
}

export interface BypassStatus {
    account: AccountWithCode;
    requestor: EventPerson | null;
    requested: string | null;
    response: string | null;
    status: string;
    displayCode: string;
    reason: string | null;
}

export interface AuditorRequestStatus {
    auditor: Auditor;
    requestor: EventPerson | null;
    requested: string | null;
    response: string | null;
    status: string;
    displayCode: string;
    responded: string | null;
}

export interface EventsState {
    selectedEvents: AfrEvents | null;
    status: 'idle' | 'loading' | 'failed';
}

const initialState: EventsState = {
    selectedEvents: null,
    status: "idle"
}

export const getDiscrepancyEvents = function (currentAfr: Afr | null,
                                              selectedEntity: Entity | null,
                                              currentAfrEvents: AfrEvents | null): DiscrepancyEvent | null {

    // Populate the discrepancy events if any from the AFR Events
    var newDiscrepancyEvent: DiscrepancyEvent | null = null;
    var role = "DISCREPANCY_MESSAGE";
    var list: Array<MessageEvent> = [];

    if (currentAfr && selectedEntity && currentAfrEvents) {

        newDiscrepancyEvent = {
            afrId: currentAfr.afrId,
            entityName: selectedEntity.name,
            firstDiscrepancy: null,
            secondDiscrepancy: null,
            thirdDiscrepancy: null
        }


        if (currentAfrEvents.messageEvents && currentAfrEvents.messageEvents.length > 0) {

            list = currentAfrEvents.messageEvents.filter(x => x.role === role);
            if (list && list.length > 0) {

                // order the events asc by timestamp 
                list.sort((a, b) => (a.timestamp > b.timestamp) ? 1 : ((b.timestamp > a.timestamp) ? -1 : 0));
                var i = 1;

                for (var event of list) {
                    //Load discrepancies  
                    if (i === 1) {
                        newDiscrepancyEvent.firstDiscrepancy = event;
                    }
                    if (i === 2) {
                        newDiscrepancyEvent.secondDiscrepancy = event;
                    }
                    if (i === 3) {
                        newDiscrepancyEvent.thirdDiscrepancy = event;
                    }
                    i = i + 1;
                }
            }

        }
        return newDiscrepancyEvent;

    }

    return null;

}
const getEventRoleUserName = function (e: any): string {
    return utilityHelper.getRoleUserName(e.creator);
}
export const generateVersionEventList = function (vevents: Array<AfrVersionStateChange> | null) {
    // get all the documents linked to a AFR
    var list: Array<EventGeneric> = [];
    var status: string;
    //get status event list
    if (vevents) {

        var lastEventSignature : string = "";
        var rank: number = 0;
        // pull the event information
        for (var e of vevents) {
            status = e.statevalue;
            var eventUserName = getEventRoleUserName(e);
            var eventSignature: string = eventUserName + "_" + status + "_" + e.timestamp;
            if (eventSignature !== lastEventSignature) {
                lastEventSignature = eventSignature;
                // The rank is appended to the timestamp so otherwise identical timestamps will be sorted by order. Since this value is appended to the end of the millisecond part of timestamp there's
                // no reall chance of a timestamp with a rank appended inadvertantly matching a timestamp for an unrelated event.
                rank = rank + 1;
               list.push({ user: eventUserName, type: status, date: e.timestamp + rank, detail: e.version });
            }
        }
    }



    return list;
    /*TODO: continue gathering the list of events from other sources  */

}

export const getCommentEventList = function (currentEvent: AfrEvents | null) {

    // get all the documents linked to a AFR
    var list: Array<EventGeneric> = [];
    var status: string;
    var name: string;
    var comment: string;

    //get status event list
    if (currentEvent && currentEvent.commentEvents) {
        var comments = currentEvent.commentEvents;

        // pull the event information
        for (var e of comments) {
            status = "comment";
            name = "";
            comment = "";
            if (e.creator && e.creator.lastName) {
                name = getEventRoleUserName(e);
            }
            if (e.comment) {
                comment = e.comment;
            }
            list.push({ user: name, type: status, date: e.timestamp, detail: comment });
        }
    }

    return list;

}


export const getMessageEventList = function (currentEvent: AfrEvents | null) {

    // get all the documents linked to a AFR
    var list: Array<EventGeneric> = [];
    var status: string;
    var name: string;
    var subject: string;

    //get status event list
    if (currentEvent && currentEvent.messageEvents) {
        var message = currentEvent.messageEvents;

        // pull the message information
        for (var msg of message) {
            status = "message";
            name = "";
            subject = "";
            if (msg.creator && msg.creator.lastName) {
                name = getEventRoleUserName(msg);
            }
            if (msg.message && msg.message.subject) {
                subject = utilityHelper.toTitleCase(msg.message.subject)
                    .replace(" For:", " for:")
                    .replace(" Dfs", " DFS")
                    .replace(" By ", " by ");
            }

            list.push({ user: name, type: status, date: msg.timestamp, detail: subject });
        }
    }

    return list;

}



export const loadEvents = createAsyncThunk(
    "Afr/GetEvents",
    async (afrReference: string): Promise<AfrEvents | null> => {
        try {
            const result = await http<undefined, AfrEvents>({
                path: `/events/${afrReference}`,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const postDiscrepancy = createAsyncThunk(
    "Events/PostEmail",
    async (mailInput: EmailEventInput): Promise<MessageEvent | null> => {
        try {
            const result = await http<EmailEventInput, MessageEvent>({
                path: `/events/postDescrepancyEmail`,
                method: 'post',
                body: mailInput
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const getEmailStageByAfrYear = createAsyncThunk(
    "Events/GetEmailStageByAfrYear",
    async (reportYear: { item1: number, item2: string }): Promise<Array<afrEmailStage> | null> => {
        try {
            const result = await http<{ item1: number, item2: string }, Array<afrEmailStage>>({
                path: `/events/getEmailStageByAfrYear`,
                method: 'post',
                body: reportYear
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const saveAfrEmailStageGivenAfrYear = createAsyncThunk(
    "Events/SaveAfrEmailStageGivenAfrYear",
    async (emailStageByYear: { item1: number; item2: afrEmailStageInput }): Promise<afrEmailStage | null> => {
        try {
            const result = await http<{ item1: number; item2: afrEmailStageInput }, afrEmailStage>({
                path: `/events/saveAfrEmailStageGivenAfrYear`,
                method: 'post',
                body: emailStageByYear
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const advanceAfrEmailStageGivenAfrYear = createAsyncThunk(
    "Events/AdvanceAfrEmailStageGivenAfrYear",
    async (emailStageByYear: { item1: number; item2: afrEmailStageInput }): Promise<afrEmailStage | null> => {
        try {
            const result = await http<{ item1: number; item2: afrEmailStageInput }, afrEmailStage>({
                path: `/events/advanceAfrEmailStageGivenAfrYear`,
                method: 'post',
                body: emailStageByYear
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });
    



export const sendAutoReminderReportTypeEmail = createAsyncThunk
    ("Events/SendAutoReminderReportTypeEmail",
        async(scheduledDate: { item1: string; item2: string }): Promise<autoReminderEmailScheduler | null> => {
            try {
                const result = await http<{ item1: string; item2: string }, autoReminderEmailScheduler >({
            path: `/events/sendAutoReminderReportTypeEmail`,
            method: 'post',
            body: scheduledDate
        });
        if (result.ok && result.parsedBody) {
            return result.parsedBody;
        }
        else {
            return null;
        }
    }
    catch (ex) {
        return null;
    }
});


export const postMail = createAsyncThunk(
    "Events/PostMail",
    async (mailInput: EmailEventInput): Promise<MessageEvent | null> => {
        try {
            const result = await http<EmailEventInput, MessageEvent>({
                path: `/events/postEmail`,
                method: 'post',
                body: mailInput
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const requestBypass = createAsyncThunk(
    "Events/RequestBypass",
    async (mailInput: ActionRequest): Promise<MessageEvent | null> => {
        try {
            const result = await http<ActionRequest, MessageEvent>({
                path: `/events/requestBypass`,
                method: 'post',
                body: mailInput
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const requestCPAFirm = createAsyncThunk(
    "Events/RequestCPAFirm",
    async (newAuditorInput: { item1: string; item2: AuditorDetailsInput; item3: number; }): Promise<MessageEvent | null> => {
        try {
            const result = await http<{ item1: string; item2: AuditorDetailsInput; item3: number }, MessageEvent>({
                path: `/events/requestCPAFirm`,
                method: 'post',
                body: newAuditorInput
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const respondCPAFirm = createAsyncThunk(
    "Events/RespondCPAFirm",
    async (newAuditorInput: { item1: string; item2: AuditorDetailsInput; item3: string }): Promise<MessageEvent | null> => {
        try {
            const result = await http<{ item1: string; item2: AuditorDetailsInput }, MessageEvent>({
                path: `/events/respondCPAFirm`,
                method: 'post',
                body: newAuditorInput
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const sendBypassResponse = createAsyncThunk(
    "Events/SendBypassResponse",
    async (mailInput: ActionResponseRequest): Promise<MessageEvent | null> => {
        try {
            const result = await http<ActionResponseRequest, MessageEvent>({
                path: `/events/sendBypassResponse`,
                method: 'post',
                body: mailInput
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const sendMultipleBypassResponse = createAsyncThunk(
    "Events/SendMultipleBypassResponse",
    async (mailInput: MultipleBypassResponseRequest): Promise<MessageEvent[]> => {
        try {
            console.log("request", mailInput);
            const result = await http<MultipleBypassResponseRequest, MessageEvent[] >({
                path: `/events/sendMultipleBypassResponse`,
                method: 'post',
                body: mailInput
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return [];
            }
        } catch (ex) {
            return [];
        }
    });

export const postComment = createAsyncThunk(
    "Events/PostComment",
    async (req: PostCommentRequest): Promise<CommentEvent | null> => {
        try {
            const result = await http<PostCommentRequest, CommentEvent>({
                path: `/events/postComment`,
                method: 'post',
                body: req
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const sendErrorToDevelopers = createAsyncThunk(
    "MacroError/sendErrorToDevelopers",
    async (errorData: ErrorData): Promise<boolean | undefined> => {
        
        const result = await http<ErrorData, boolean>({
            path: `/events/postErrorEmail`,
            method: 'post',
            body: errorData
        });

        return result.parsedBody;
    });

export const eventsSlice = createSlice({
    name: 'Events',
    initialState,
    reducers: {
        resetEventsState: (state: any, action: any) => {
            return initialState
        },
        setToSelectedEvents: (state: any, action: any) => {
            state.selectedEvents = action.payload;
        }
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(loadEvents.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(loadEvents.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.selectedEvents = action.payload;
            })
            .addCase(postMail.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(postMail.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                if (action.payload && state.selectedEvents) {
                    state.selectedEvents.messageEvents.push(action.payload);
                }
            })
            .addCase(requestBypass.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(requestBypass.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                if (action.payload && state.selectedEvents) {
                    state.selectedEvents.messageEvents.push(action.payload);
                }
            })
            .addCase(sendBypassResponse.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(sendBypassResponse.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                if (action.payload && state.selectedEvents) {
                    state.selectedEvents.messageEvents.push(action.payload);
                }
            })
            .addCase(sendMultipleBypassResponse.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(sendMultipleBypassResponse.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                if (action.payload && state.selectedEvents) {
                    action.payload.map((x : any) => state.selectedEvents.messageEvents.push(x));
                }
            })
            .addCase(postComment.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(postComment.fulfilled, (state: EventsState, action: any) => {
                state.status = "idle";
                if (action.payload && state.selectedEvents) {
                    var comment: CommentEvent = action.payload;
                    state.selectedEvents.commentEvents.push(comment);
                }
            })
            .addCase(postDiscrepancy.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(postDiscrepancy.fulfilled, (state: EventsState, action: any) => {
                state.status = "idle";
                if (action.payload && state.selectedEvents) {
                    state.selectedEvents.messageEvents.push(action.payload);
                }
            })
            .addCase(requestCPAFirm.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(requestCPAFirm.fulfilled, (state: EventsState, action: any) => {
                state.status = "idle";
                if (action.payload && state.selectedEvents) {
                    state.selectedEvents.messageEvents.push(action.payload);
                }
            })
            .addCase(getEmailStageByAfrYear.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getEmailStageByAfrYear.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
            .addCase(saveAfrEmailStageGivenAfrYear.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(saveAfrEmailStageGivenAfrYear.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
            .addCase(advanceAfrEmailStageGivenAfrYear.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(advanceAfrEmailStageGivenAfrYear.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
      
    },
});

export const { resetEventsState, setToSelectedEvents } = eventsSlice.actions;

export const getSelectedEvents = (state: RootState) => state.events ? state.events.selectedEvents : null;

export const getMessages = (state: RootState) => state.events ? state.events.selectedEvents ? state.events.selectedEvents.messageEvents : null : null;

export const getComments = (state: RootState) => state.events ? state.events.selectedEvents ? state.events.selectedEvents.commentEvents : null : null;

export const getAssignments = (state: RootState) => state.events ? state.events.selectedEvents ? state.events.selectedEvents.assignmentEvents : null : null;

export const getVersionHistory = (state: RootState) => state.events ? state.events.selectedEvents ? state.events.selectedEvents.versionEvents : null : null;

export const getVersionEvents = (state: RootState) => state.events ? state.events.selectedEvents ? generateVersionEventList(state.events.selectedEvents.versionEvents) : [] : [];

export default eventsSlice.reducer;